import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Zap, X, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { fetchTeams } from "../lib/api";
import { Flag } from "../components/Flag";

const STADIUM_IMG =
  "https://images.unsplash.com/photo-1599158150601-1417ebbaafdd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxzb2NjZXIlMjBzdGFkaXVtJTIwZGFyayUyMG5pZ2h0JTIwZHJhbWF0aWN8ZW58MHx8fHwxNzgxMDMwMTg4fDA&ixlib=rb-4.1.0&q=85";

const CONFEDS = ["ALL", "UEFA", "CONMEBOL", "CONCACAF", "AFC", "CAF", "OFC"];

export default function Home() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [confed, setConfed] = useState("ALL");
  const [picked, setPicked] = useState([]);
  const [knockout, setKnockout] = useState(false);

  useEffect(() => {
    fetchTeams()
      .then((t) => {
        setTeams(t);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return teams.filter((t) => {
      if (confed !== "ALL" && t.confederation !== confed) return false;
      if (query && !t.name.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [teams, confed, query]);

  const togglePick = (code) => {
    setPicked((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      if (prev.length >= 2) return [prev[1], code];
      return [...prev, code];
    });
  };

  const startSim = () => {
    if (picked.length !== 2) return;
    const params = new URLSearchParams({ a: picked[0], b: picked[1], k: knockout ? "1" : "0" });
    navigate(`/match?${params.toString()}`);
  };

  const teamA = teams.find((t) => t.code === picked[0]);
  const teamB = teams.find((t) => t.code === picked[1]);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section
        className="relative overflow-hidden border-b border-zinc-800"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${STADIUM_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/70 to-black" />
        <div className="grid-bg absolute inset-0 opacity-50" />

        <div className="relative mx-auto max-w-[1400px] px-6 py-20 sm:py-28">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-volt">
            FIFA World Cup 2026 · USA · Canada · Mexico
          </div>
          <h1 className="mt-4 font-display text-5xl font-black uppercase leading-[0.85] tracking-tighter sm:text-7xl lg:text-8xl">
            Simulate
            <br />
            The <span className="text-volt">Tournament</span>
          </h1>
          <p className="mt-6 max-w-xl font-body text-base text-zinc-400 sm:text-lg">
            A data-driven match engine powered by squad market value, Top-5 league presence,
            historical pedigree, age curves and current form. Pick any two of the 48 qualified
            nations and watch the match unfold minute-by-minute.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6 font-mono text-xs uppercase tracking-widest text-zinc-500">
            <span><span className="text-volt">35%</span> Squad Value</span>
            <span><span className="text-volt">20%</span> Top-5 Leagues</span>
            <span><span className="text-volt">20%</span> Pedigree</span>
            <span><span className="text-volt">15%</span> Age Curve</span>
            <span><span className="text-volt">10%</span> Form</span>
          </div>
        </div>
      </section>

      {/* Selection panel */}
      <section className="mx-auto max-w-[1400px] px-6 py-12">
        {/* Picked Teams Strip */}
        <div className="mb-8 grid grid-cols-1 gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-[1fr_auto_1fr]">
          <PickSlot label="HOME" team={teamA} onClear={() => setPicked(picked.filter((c) => c !== picked[0]))} testid="pick-slot-a" />
          <div className="flex items-center justify-center bg-black px-8 py-4">
            <span className="font-display text-2xl font-black text-volt">VS</span>
          </div>
          <PickSlot label="AWAY" team={teamB} onClear={() => setPicked(picked.filter((c) => c !== picked[1]))} testid="pick-slot-b" />
        </div>

        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              Step 1
            </div>
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight">
              Pick two nations
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <label
              data-testid="knockout-toggle"
              className={`flex cursor-pointer items-center gap-2 border px-3 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                knockout
                  ? "border-volt bg-volt text-black"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={knockout}
                onChange={(e) => setKnockout(e.target.checked)}
              />
              Knockout Mode
            </label>
            <Button
              data-testid="simulate-now-btn"
              onClick={startSim}
              disabled={picked.length !== 2}
              className="h-11 rounded-none bg-volt px-6 font-display text-base font-bold uppercase tracking-widest text-black hover:bg-white disabled:bg-zinc-800 disabled:text-zinc-600"
            >
              <Zap className="mr-2 h-4 w-4" /> Simulate Now <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="team-search"
              placeholder="Search nation..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="rounded-none border-zinc-800 bg-zinc-900/60 pl-9 font-body text-sm focus-visible:ring-volt"
            />
          </div>
          <Tabs value={confed} onValueChange={setConfed}>
            <TabsList className="rounded-none border border-zinc-800 bg-zinc-900/40 p-0">
              {CONFEDS.map((c) => (
                <TabsTrigger
                  key={c}
                  value={c}
                  data-testid={`confed-tab-${c}`}
                  className="rounded-none px-3 font-mono text-xs uppercase tracking-widest data-[state=active]:bg-volt data-[state=active]:text-black"
                >
                  {c}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Team Grid */}
        {loading ? (
          <div className="grid grid-cols-2 gap-px bg-zinc-800 md:grid-cols-4 lg:grid-cols-6">
            {Array.from({ length: 18 }).map((_, i) => (
              <div key={i} className="h-32 animate-pulse bg-zinc-900" />
            ))}
          </div>
        ) : (
          <div
            data-testid="team-grid"
            className="grid grid-cols-2 gap-px bg-zinc-800 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
          >
            {filtered.map((t) => {
              const isPicked = picked.includes(t.code);
              return (
                <button
                  key={t.code}
                  data-testid={`team-card-${t.code}`}
                  onClick={() => togglePick(t.code)}
                  className={`group relative flex flex-col items-start gap-2 bg-zinc-950 p-4 text-left transition-colors hover:bg-zinc-900 ${
                    isPicked ? "ring-2 ring-inset ring-[#CCFF00]" : ""
                  }`}
                >
                  <div className="flex w-full items-center justify-between">
                    <Flag code={t.flag} className="h-5 w-7 object-cover" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {t.confederation}
                    </span>
                  </div>
                  <div className="font-display text-xl font-extrabold uppercase leading-tight">
                    {t.name}
                  </div>
                  {t.qualification === "playoff" && (
                    <span
                      className="font-mono text-[9px] uppercase tracking-widest text-amber-400"
                      title="Qualified via playoff"
                    >
                      via playoff
                    </span>
                  )}
                  {t.qualification === "host" && (
                    <span className="font-mono text-[9px] uppercase tracking-widest text-bluez">
                      host nation
                    </span>
                  )}
                  <div className="mt-auto flex w-full items-end justify-between pt-2">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        MSS
                      </div>
                      <div className="font-display text-xl font-black text-volt tabular-nums">
                        {t.mss.mss.toFixed(0)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        Value
                      </div>
                      <div className="font-body text-sm font-bold tabular-nums">
                        €{t.squad_value_m}M
                      </div>
                    </div>
                  </div>
                  {isPicked && (
                    <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center bg-volt font-display text-xs font-black text-black">
                      ✓
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function PickSlot({ label, team, onClear, testid }) {
  return (
    <div className="relative flex items-center gap-4 bg-black p-6" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</div>
      {team ? (
        <>
          <Flag code={team.flag} className="h-8 w-12 object-cover" />
          <div className="flex-1">
            <div className="font-display text-2xl font-black uppercase leading-tight">{team.name}</div>
            <div className="font-mono text-xs text-zinc-500">
              MSS <span className="text-volt">{team.mss.mss.toFixed(1)}</span> · €{team.squad_value_m}M
            </div>
          </div>
          <button
            onClick={onClear}
            data-testid={`${testid}-clear`}
            className="text-zinc-500 hover:text-white"
            aria-label="Clear selection"
          >
            <X className="h-5 w-5" />
          </button>
        </>
      ) : (
        <div className="flex flex-1 items-center gap-3 text-zinc-600">
          <div className="h-8 w-12 border border-dashed border-zinc-700" />
          <span className="font-display text-xl font-extrabold uppercase tracking-tight">
            Select a team
          </span>
        </div>
      )}
    </div>
  );
}
